import React from 'react'

import { Box } from '@factorialco/deprecated-design-system'

import DayPicker from 'modules/core/components/DayPicker'
import Select from 'modules/core/components/Select'
import TextField from 'modules/core/components/TextField'
import countryList from 'modules/core/lib/countryList'
import { dateToString, dateUIToBackend, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import ToggleSection from 'modules/employees/components/Personal/NewPersonal/ToggleSection/index'

import { ResidenceValues } from '../ResidenceValues'

interface Props {
  values: ResidenceValues
  onChangeValue: (fieldName: string, fieldValue: Record<string, unknown> | undefined) => void
  readonly: boolean
}

const TaxResidenceSection: React.FC<Props> = ({ values, onChangeValue, readonly }) => {
  const optionalPlaceholder = i18n.t('common.optional')

  const handleTaxFieldChanged = (fieldName: string, fieldValue: string | undefined | boolean) => {
    onChangeValue('taxResidence', {
      attributes: { ...values.taxResidence?.attributes, [fieldName]: fieldValue },
    })
  }

  const openValue = !!values.taxResidence && !values.taxResidence?.attributes.is_resident

  return (
    <ToggleSection
      description={i18n.t('employee_form.residence_section.fiscal_residence_different')}
      openValue={openValue}
      toggleAction={(value) => handleTaxFieldChanged('is_resident', value)}
    >
      <Box gap="s24">
        <Box flexDirection="row" gap="s24">
          <TextField
            label={i18n.t('address_input.address_line_1')}
            value={values.taxResidence?.attributes.address_line_1}
            readonly={readonly}
            onChange={(value) => handleTaxFieldChanged('address_line_1', value)}
          />
          <TextField
            label={i18n.t('address_input.address_line_2')}
            value={values.taxResidence?.attributes.address_line_2}
            readonly={readonly}
            onChange={(value) => handleTaxFieldChanged('address_line_2', value)}
            placeholder={optionalPlaceholder}
          />
        </Box>
        <Box flexDirection="row" gap="s24">
          <TextField
            label={i18n.t('address_input.zip')}
            value={values.taxResidence?.attributes.postal_code}
            readonly={readonly}
            onChange={(value) => handleTaxFieldChanged('postal_code', value)}
          />
          <TextField
            label={i18n.t('address_input.city')}
            value={values.taxResidence?.attributes.city}
            readonly={readonly}
            onChange={(value) => handleTaxFieldChanged('city', value)}
          />
          <TextField
            label={i18n.t('address_input.state')}
            value={values.taxResidence?.attributes.state}
            readonly={readonly}
            onChange={(value) => handleTaxFieldChanged('state', value)}
          />
          <Select
            none={i18n.t('common.select')}
            readonly={readonly}
            onChange={(value) => handleTaxFieldChanged('country', value)}
            label={i18n.t('address_input.country')}
            options={countryList}
            value={values.taxResidence?.attributes.country}
          />
        </Box>
        <Box flexDirection="row" gap="s24">
          <TextField
            label={i18n.t('employee_form.tax_residence.city_of_birth')}
            value={values.taxResidence?.attributes.city_of_birth}
            readonly={readonly}
            onChange={(value) => handleTaxFieldChanged('city_of_birth', value)}
          />
          <Select
            none={i18n.t('common.select')}
            readonly={readonly}
            onChange={(value) => handleTaxFieldChanged('country_of_birth', value)}
            label={i18n.t('employee_form.tax_residence.country_of_birth')}
            options={countryList}
            value={values.taxResidence?.attributes.country_of_birth}
          />
        </Box>
        <Box flexDirection="row" gap="s24">
          <TextField
            label={i18n.t('employee_form.identifier')}
            value={values.taxResidence?.attributes.identifier}
            readonly={readonly}
            onChange={(value) => handleTaxFieldChanged('identifier', value)}
          />
          <DayPicker
            label={i18n.t('employee_form.tax_residence.no_resident_end_date')}
            value={dateToString(
              stringToDate(values.taxResidence?.attributes.no_resident_end_date, 'backend'),
              'ui'
            )}
            readonly={readonly}
            onChange={(value) =>
              handleTaxFieldChanged('no_resident_end_date', dateUIToBackend(value))
            }
          />
        </Box>
      </Box>
    </ToggleSection>
  )
}

export default TaxResidenceSection
